import { SoftTag } from '../Tag/SoftTag';
import { ServiceTypeTag } from '../Tag/ServiceTypeTag';
import { BLUE, CHROME, GREEN, INK, PRIMARY } from '../Theme/palette';
import { PlusOutlined, SettingOutlined, ToolOutlined } from '@ant-design/icons';
import { ServiceOrderExtraForm } from '@components';
import { useTenantVat, useView } from '@hooks';
import { L } from '@lib';
import { InitialServiceOrderExtra, ServiceOrder, ServiceOrderExtra } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Badge, Button, Form, Modal, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';

const { Text } = Typography;

interface IProps {
  serviceOrders: ServiceOrder[];
  serviceOrderExtras: ServiceOrderExtra[];
  handleServiceOrdersUpdate?: (serviceOrder: ServiceOrder) => void;
}

const sectionStyle: React.CSSProperties = {
  background: `linear-gradient(135deg, ${CHROME.bg} 0%, ${CHROME.bgLight} 100%)`,
  borderRadius: 10,
  border: `1px solid ${CHROME.border}`,
  padding: '16px 16px 12px',
  marginBottom: 8,
};

const iconWrapperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 6,
  background: PRIMARY,
  color: '#fff',
  fontSize: 14,
};

const headerLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#8c8c8c',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};

export const InvoiceServiceList: React.FC<IProps> = observer(({
  serviceOrders,
  serviceOrderExtras,
  handleServiceOrdersUpdate,
}) => {
  const { isMobile } = useView();
  const vat = useTenantVat();
  const { orderStore, serviceStore } = useStore();
  const { selectServices } = serviceStore;
  const [addForm] = Form.useForm();
  const [showAddModal, setShowAddModal] = useState(false);

  const handleAdd = () => {
    addForm.setFieldsValue({
      ...InitialServiceOrderExtra,
      type: 'Part',
      serviceOrderId: serviceOrders.length === 1 ? serviceOrders[0].id : undefined,
    });
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    addForm.resetFields();
  };

  const handleCreate = useCallback(
    async (values: any) => {
      const resetFormAndCloseModal = () => {
        setShowAddModal(false);
        addForm.resetFields();
      };

      const updateServiceOrder = (updatedOrder: any) => {
        if (updatedOrder && handleServiceOrdersUpdate) {
          handleServiceOrdersUpdate(updatedOrder);
        }
      };

      const isService = addForm.getFieldValue('type') === 'Service';

      if (isService) {
        const selectedService = selectServices.get(values.serviceId);
        const vatValue = selectedService?.isVat ? vat : 0;

        const result = await orderStore.createServiceOrder({
          ...values,
          vat: vatValue,
          extras: [],
          orderId: serviceOrders[0]?.orderId,
        });

        resetFormAndCloseModal();
        if (!result) return;

        const updatedOrder = serviceOrders.find((order) => order.id === result.id);
        updateServiceOrder(updatedOrder);
      } else {
        const result = await orderStore.createExtra({
          ...values,
          vat,
        });

        resetFormAndCloseModal();
        if (!result) return;

        const updatedOrder = serviceOrders.find((order) => order.id === result.serviceOrderId);
        if (updatedOrder) {
          updatedOrder.extras.push(result);
          updateServiceOrder(updatedOrder);
        }
      }
    },
    [orderStore, addForm, vat, serviceOrders, selectServices, handleServiceOrdersUpdate]
  );

  const getExtrasForService = (serviceOrderId: string) =>
    serviceOrderExtras.filter((e) => e.serviceOrderId === serviceOrderId);

  const calculateServiceTotal = (so: ServiceOrder) => {
    const labour = Utils.serviceOrderLine(so);
    const partsTotal = getExtrasForService(so.id)
      .map((extra) => Utils.extraLine(extra))
      .reduce((acc, part) => acc + part.net + part.vat, 0);

    return Utils.roundToTwoDecimals(labour.net + labour.vat + partsTotal);
  };

  const {
    labour: grandLabour,
    parts: grandParts,
    vat: grandVat,
    total: grandTotal,
  } = Utils.summariseServiceOrders(serviceOrders, serviceOrderExtras);

  // Grid matching OrderServiceSelect: Name | Price | Qty/Dur | Disc | Sub | VAT | Total
  const gridCols = '1fr 80px 70px 70px 90px 90px 100px';

  const addExtraButton = handleServiceOrdersUpdate ? (
    <Button
      size="large"
      onClick={(e) => {
        e.currentTarget.blur();
        handleAdd();
      }}
      icon={<PlusOutlined />}
      style={{ backgroundColor: GREEN.badge, borderColor: GREEN.badge, color: '#fff' }}
    >
      {L('Add extra')}
    </Button>
  ) : null;

  const addExtraModal = handleServiceOrdersUpdate ? (
    <Modal
      onCancel={handleCloseAddModal}
      footer={null}
      open={showAddModal}
      width={560}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: GREEN.soft,
              border: `1px solid ${GREEN.card}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PlusOutlined style={{ fontSize: 16, color: GREEN.badge }} />
          </div>
          <span style={{ fontSize: 16, fontWeight: 600 }}>{L('Add extra')}</span>
        </div>
      }
      styles={{
        header: { borderBottom: '1px solid #f0f0f0', paddingBottom: 12 },
        body: { paddingTop: 16 },
      }}
    >
      <ServiceOrderExtraForm
        form={addForm}
        serviceOrders={serviceOrders}
        onFinish={handleCreate}
        onCancel={handleCloseAddModal}
      />
    </Modal>
  ) : null;

  const renderServiceCard = (so: ServiceOrder) => {
    const { net: subTotal, vat: vatAmount } = Utils.serviceOrderLine(so);
    const extras = getExtrasForService(so.id);

    return (
      <div
        key={so.id}
        style={{
          background: '#fff',
          border: `1px solid ${CHROME.border}`,
          borderRadius: 8,
          padding: '8px 0',
          marginBottom: 8,
        }}
      >
        {/* Service row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: 8,
            alignItems: 'end',
            padding: '6px 10px',
            marginBottom: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4 }}>
            <ToolOutlined style={{ fontSize: 13, color: PRIMARY }} />
            <Text style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
              {so.title}
            </Text>
            <ServiceTypeTag
              isFixed={so.isFixed}
              style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}
            />
            {so.note && (
              <Text style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', fontStyle: 'italic' }}>
                {so.note}
              </Text>
            )}
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Price (£)</Text>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#555', display: 'block' }}>
              {Utils.formatCurrency(so.price)}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Hrs / Qty</Text>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#555', display: 'block' }}>
              {so.isFixed ? '—' : `${so.duration}h`}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Disc. %</Text>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#555', display: 'block' }}>
              {so.discount > 0 ? `${so.discount}%` : '—'}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Sub Total</Text>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#555', display: 'block' }}>
              {Utils.formatCurrency(subTotal)}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>VAT</Text>
            <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)', display: 'block' }}>
              {Utils.formatCurrency(vatAmount)}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Total</Text>
            <SoftTag
              group="blue"
              style={{
                fontSize: 13,
                fontWeight: 700,
                padding: '2px 8px',
                margin: 0,
                minWidth: 70,
                textAlign: 'center',
                display: 'inline-block',
              }}
            >
              {Utils.formatCurrency(subTotal + vatAmount)}
            </SoftTag>
          </div>
        </div>

        {/* Extras/Parts */}
        {extras.length > 0 && (
          <div style={{ borderTop: '1px solid #e8eef5', margin: '0 10px' }} />
        )}
        {extras.map((extra, idx) => {
          const { net: partSub, vat: partVat } = Utils.extraLine(extra);
          return (
            <div
              key={extra.id}
              style={{
                display: 'grid',
                gridTemplateColumns: gridCols,
                gap: 8,
                alignItems: 'center',
                padding: '6px 10px',
                background: idx % 2 === 0 ? '#fafcff' : '#fff',
                borderBottom: idx < extras.length - 1 ? '1px solid #f0f4f8' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <SettingOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                <Text style={{ fontSize: 13, color: '#555' }}>{extra.name}</Text>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: '#555' }}>
                  {Utils.formatCurrency(extra.price)}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: '#555' }}>
                  {extra.quantity}{extra.type === 'Fluid' ? 'L' : ''}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: '#555' }}>
                  {extra.discount > 0 ? `${extra.discount}%` : '—'}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: '#555' }}>
                  {Utils.formatCurrency(partSub)}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
                  {Utils.formatCurrency(partVat)}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <SoftTag
                  group="blue"
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    padding: '2px 8px',
                    margin: 0,
                    minWidth: 70,
                    textAlign: 'center',
                    display: 'inline-block',
                  }}
                >
                  {Utils.formatCurrency(partSub + partVat)}
                </SoftTag>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMobileServiceCard = (so: ServiceOrder) => {
    const { net: subTotal, vat: vatAmount } = Utils.serviceOrderLine(so);
    const extras = getExtrasForService(so.id);
    const total = calculateServiceTotal(so);

    const infoRow = (label: string, value: string) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {label}
        </Text>
        <Text style={{ fontSize: 13, fontWeight: 500 }}>{value}</Text>
      </div>
    );

    return (
      <div
        key={so.id}
        style={{
          background: '#fff',
          border: `1px solid ${CHROME.border}`,
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <Text strong style={{ fontSize: 14 }}>
            {so.title}
          </Text>
          <SoftTag
            group="blue"
            style={{ margin: 0, fontSize: 13, fontWeight: 700, padding: '2px 8px' }}
          >
            {Utils.formatCurrency(total)}
          </SoftTag>
        </div>
        <ServiceTypeTag isFixed={so.isFixed} style={{ margin: '0 0 8px', fontSize: 11 }} />
        {infoRow('Price', Utils.formatCurrency(so.price))}
        {!so.isFixed && infoRow('Hours', String(so.duration))}
        {so.discount > 0 && infoRow('Discount', `${so.discount}%`)}
        {infoRow('Subtotal', Utils.formatCurrency(subTotal))}
        {infoRow('VAT', Utils.formatCurrency(vatAmount))}
        {so.note && (
          <Text
            type="secondary"
            style={{ fontSize: 12, fontStyle: 'italic', display: 'block', marginTop: 4 }}
          >
            Note: {so.note}
          </Text>
        )}
        {extras.length > 0 && (
          <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
            <Text
              strong
              style={{
                fontSize: 12,
                color: '#8c8c8c',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
              }}
            >
              Parts & Extras
            </Text>
            {extras.map((extra) => {
              const { net: partSub, vat: partVat } = Utils.extraLine(extra);
              return (
                <div
                  key={extra.id}
                  style={{
                    marginTop: 6,
                    paddingLeft: 8,
                    borderLeft: `2px solid ${CHROME.border}`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ fontSize: 13 }}>{extra.name}</Text>
                    <Text strong style={{ fontSize: 13 }}>
                      {Utils.formatCurrency(partSub + partVat)}
                    </Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {Utils.formatCurrency(extra.price)} x {extra.quantity}{extra.type === 'Fluid' ? 'L' : ''}
                    {extra.discount > 0 ? ` (-${extra.discount}%)` : ''}
                  </Text>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (isMobile) {
    return (
      <div style={{ ...sectionStyle, padding: '12px 8px 8px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 10,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                ...iconWrapperStyle,
                width: 24,
                height: 24,
                borderRadius: 5,
                fontSize: 12,
              }}
            >
              <ToolOutlined />
            </div>
            <Text style={{ fontSize: 14, fontWeight: 700, color: INK }}>Services</Text>
            <Badge
              count={serviceOrders.length}
              style={{ backgroundColor: PRIMARY, fontSize: 10, fontWeight: 600 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#555' }}>
              L: <strong>{Utils.formatCurrency(grandLabour)}</strong>
            </span>
            <span style={{ fontSize: 11, color: '#555' }}>
              P: <strong>{Utils.formatCurrency(grandParts)}</strong>
            </span>
            <span style={{ fontSize: 11, color: '#555' }}>
              VAT: <strong>{Utils.formatCurrency(grandVat)}</strong>
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: BLUE.tag,
                background: BLUE.soft,
                border: `1px solid ${BLUE.card}`,
                padding: '2px 10px',
                borderRadius: 4,
              }}
            >
              {Utils.formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
        {serviceOrders.map(renderMobileServiceCard)}
        {addExtraButton}
        {addExtraModal}
      </div>
    );
  }

  return (
    <div style={sectionStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={iconWrapperStyle}>
            <ToolOutlined />
          </div>
          <Text style={{ fontSize: 15, fontWeight: 700, color: INK, letterSpacing: 0.2 }}>
            Services
          </Text>
          <Badge
            count={serviceOrders.length}
            style={{
              backgroundColor: PRIMARY,
              fontSize: 11,
              fontWeight: 600,
              boxShadow: '0 1px 4px rgba(44, 189, 255, 0.3)',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {addExtraButton}
          <div
            style={{
              display: 'flex',
              gap: 16,
              fontSize: 14,
              alignItems: 'center',
              background: '#fff',
              border: `1px solid ${CHROME.border}`,
              borderRadius: 8,
              padding: '8px 16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <span style={{ color: '#555' }}>
              Labour:{' '}
              <strong style={{ color: INK }}>{Utils.formatCurrency(grandLabour)}</strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              Parts:{' '}
              <strong style={{ color: INK }}>{Utils.formatCurrency(grandParts)}</strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              VAT: <strong style={{ color: INK }}>{Utils.formatCurrency(grandVat)}</strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <SoftTag
              group="blue"
              style={{ fontSize: 15, fontWeight: 700, padding: '2px 12px', margin: 0 }}
            >
              {Utils.formatCurrency(grandTotal)}
            </SoftTag>
          </div>
        </div>
      </div>

      {serviceOrders.map(renderServiceCard)}
      {addExtraModal}
    </div>
  );
});
